import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Edit, Trash2, Loader2, Search, X, AlertTriangle, Brain, Zap, Heart } from 'lucide-react';

interface NutrientDeficiency {
  id: string;
  nutrient: string;
  category: string;
  somatic_symptoms: string[];
  vitality_symptoms: string[];
  consciousness_symptoms: string[];
  context_symptoms: string[];
  severity: string | null;
  reversibility: string | null;
  high_risk_groups: string[];
  prevalence_notes: string | null;
  source_references: string[];
  fortification_tags: string[];
  key_biomarkers: string | null;
  is_active: boolean;
  sort_order: number;
}

const defaultFormData = {
  nutrient: '',
  category: 'Vitamin',
  somatic_symptoms: [] as string[],
  vitality_symptoms: [] as string[],
  consciousness_symptoms: [] as string[],
  context_symptoms: [] as string[],
  severity: '',
  reversibility: '',
  high_risk_groups: [] as string[],
  prevalence_notes: '',
  source_references: [] as string[],
  fortification_tags: [] as string[],
  key_biomarkers: '',
  is_active: true,
  sort_order: 0,
};

const categories = ['Vitamin', 'Mineral', 'Electrolyte', 'Macronutrient', 'Fatty Acid', 'Amino Acid', 'Other', 'social', 'environmental'];
const severityLevels = ['Mild', 'Moderate', 'Severe', 'Mild-Moderate', 'Moderate-Severe', 'High'];
const reversibilityOptions = ['Yes', 'Partially', 'No', 'Partially reversible', 'Reversible'];

const AdminNutrientDatabase = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNutrient, setEditingNutrient] = useState<NutrientDeficiency | null>(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Array input states
  const [somaticInput, setSomaticInput] = useState('');
  const [vitalityInput, setVitalityInput] = useState('');
  const [consciousnessInput, setConsciousnessInput] = useState('');
  const [contextInput, setContextInput] = useState('');
  const [riskGroupInput, setRiskGroupInput] = useState('');
  const [referenceInput, setReferenceInput] = useState('');
  const [fortificationInput, setFortificationInput] = useState('');

  // Fetch nutrients
  const { data: nutrients = [], isLoading } = useQuery({
    queryKey: ['admin-nutrient-deficiencies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('nutrient_deficiencies')
        .select('*')
        .order('category', { ascending: true })
        .order('nutrient', { ascending: true });

      if (error) throw error;
      return data as NutrientDeficiency[];
    },
  });

  // Filtered nutrients
  const filteredNutrients = useMemo(() => {
    return nutrients.filter(nutrient => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesNutrient = nutrient.nutrient.toLowerCase().includes(query);
        const matchesSymptoms = 
          nutrient.somatic_symptoms?.some(s => s.toLowerCase().includes(query)) ||
          nutrient.vitality_symptoms?.some(s => s.toLowerCase().includes(query)) ||
          nutrient.consciousness_symptoms?.some(s => s.toLowerCase().includes(query));
        if (!matchesNutrient && !matchesSymptoms) return false;
      }
      if (filterCategory !== 'all' && nutrient.category !== filterCategory) {
        return false;
      }
      return true;
    });
  }, [nutrients, searchQuery, filterCategory]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase
        .from('nutrient_deficiencies')
        .insert([{
          ...data,
          severity: data.severity || null,
          reversibility: data.reversibility || null,
          prevalence_notes: data.prevalence_notes || null,
          key_biomarkers: data.key_biomarkers || null,
        }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-nutrient-deficiencies'] });
      toast({ title: 'Success', description: 'Nutrient record created successfully' });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to create nutrient record', variant: 'destructive' });
      console.error(error);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase
        .from('nutrient_deficiencies')
        .update({
          ...data,
          severity: data.severity || null,
          reversibility: data.reversibility || null,
          prevalence_notes: data.prevalence_notes || null,
          key_biomarkers: data.key_biomarkers || null,
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-nutrient-deficiencies'] });
      toast({ title: 'Success', description: 'Nutrient record updated successfully' });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to update nutrient record', variant: 'destructive' });
      console.error(error);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('nutrient_deficiencies')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-nutrient-deficiencies'] });
      toast({ title: 'Success', description: 'Nutrient record deleted successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to delete nutrient record', variant: 'destructive' });
      console.error(error);
    },
  });

  const resetForm = () => {
    setFormData(defaultFormData);
    setEditingNutrient(null);
    setSomaticInput('');
    setVitalityInput('');
    setConsciousnessInput('');
    setContextInput('');
    setRiskGroupInput('');
    setReferenceInput('');
    setFortificationInput('');
  };

  const handleEdit = (nutrient: NutrientDeficiency) => {
    setEditingNutrient(nutrient);
    setFormData({
      nutrient: nutrient.nutrient,
      category: nutrient.category,
      somatic_symptoms: nutrient.somatic_symptoms || [],
      vitality_symptoms: nutrient.vitality_symptoms || [],
      consciousness_symptoms: nutrient.consciousness_symptoms || [],
      context_symptoms: nutrient.context_symptoms || [],
      severity: nutrient.severity || '',
      reversibility: nutrient.reversibility || '',
      high_risk_groups: nutrient.high_risk_groups || [],
      prevalence_notes: nutrient.prevalence_notes || '',
      source_references: nutrient.source_references || [],
      fortification_tags: nutrient.fortification_tags || [],
      key_biomarkers: nutrient.key_biomarkers || '',
      is_active: nutrient.is_active,
      sort_order: nutrient.sort_order,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this nutrient record?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingNutrient) {
      updateMutation.mutate({ id: editingNutrient.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) resetForm();
  };

  // Helper functions for array fields
  const addArrayItem = (field: keyof typeof formData, value: string, setValue: (v: string) => void) => {
    if (value.trim()) {
      const currentArray = formData[field] as string[];
      if (!currentArray.includes(value.trim())) {
        setFormData({ ...formData, [field]: [...currentArray, value.trim()] });
      }
      setValue('');
    }
  };

  const removeArrayItem = (field: keyof typeof formData, item: string) => {
    const currentArray = formData[field] as string[];
    setFormData({ ...formData, [field]: currentArray.filter(i => i !== item) });
  };

  const getSeverityColor = (severity: string | null) => {
    switch (severity?.toLowerCase()) {
      case 'severe': return 'destructive';
      case 'moderate': case 'moderate-severe': return 'default';
      case 'mild': case 'mild-moderate': return 'secondary';
      default: return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading nutrient database...
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Nutrient Deficiency Database</h1>
          <p className="text-muted-foreground">{filteredNutrients.length} of {nutrients.length} records</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Nutrient
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingNutrient ? 'Edit Nutrient Record' : 'Add New Nutrient Record'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="nutrient">Nutrient Name *</Label>
                  <Input
                    id="nutrient"
                    value={formData.nutrient}
                    onChange={(e) => setFormData({ ...formData, nutrient: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="sort_order">Sort Order</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              {/* Symptoms - Somatic */}
              <div>
                <Label className="flex items-center gap-2">
                  <Heart className="h-4 w-4" /> Somatic Symptoms (身)
                </Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={somaticInput}
                    onChange={(e) => setSomaticInput(e.target.value)}
                    placeholder="Add symptom..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('somatic_symptoms', somaticInput, setSomaticInput))}
                  />
                  <Button type="button" variant="outline" onClick={() => addArrayItem('somatic_symptoms', somaticInput, setSomaticInput)}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.somatic_symptoms.map((s) => (
                    <Badge key={s} variant="secondary" className="cursor-pointer" onClick={() => removeArrayItem('somatic_symptoms', s)}>
                      {s} <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Symptoms - Vitality */}
              <div>
                <Label className="flex items-center gap-2">
                  <Zap className="h-4 w-4" /> Vitality Symptoms (气)
                </Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={vitalityInput}
                    onChange={(e) => setVitalityInput(e.target.value)}
                    placeholder="Add symptom..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('vitality_symptoms', vitalityInput, setVitalityInput))}
                  />
                  <Button type="button" variant="outline" onClick={() => addArrayItem('vitality_symptoms', vitalityInput, setVitalityInput)}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.vitality_symptoms.map((s) => (
                    <Badge key={s} variant="secondary" className="cursor-pointer" onClick={() => removeArrayItem('vitality_symptoms', s)}>
                      {s} <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Symptoms - Consciousness */}
              <div>
                <Label className="flex items-center gap-2">
                  <Brain className="h-4 w-4" /> Consciousness Symptoms (意)
                </Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={consciousnessInput}
                    onChange={(e) => setConsciousnessInput(e.target.value)}
                    placeholder="Add symptom..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('consciousness_symptoms', consciousnessInput, setConsciousnessInput))}
                  />
                  <Button type="button" variant="outline" onClick={() => addArrayItem('consciousness_symptoms', consciousnessInput, setConsciousnessInput)}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.consciousness_symptoms.map((s) => (
                    <Badge key={s} variant="secondary" className="cursor-pointer" onClick={() => removeArrayItem('consciousness_symptoms', s)}>
                      {s} <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Symptoms - Context */}
              <div>
                <Label className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Context Symptoms (境)
                </Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={contextInput}
                    onChange={(e) => setContextInput(e.target.value)}
                    placeholder="Add symptom..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('context_symptoms', contextInput, setContextInput))}
                  />
                  <Button type="button" variant="outline" onClick={() => addArrayItem('context_symptoms', contextInput, setContextInput)}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.context_symptoms.map((s) => (
                    <Badge key={s} variant="secondary" className="cursor-pointer" onClick={() => removeArrayItem('context_symptoms', s)}>
                      {s} <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Severity & Reversibility */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="severity">Severity</Label>
                  <Select
                    value={formData.severity}
                    onValueChange={(value) => setFormData({ ...formData, severity: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Not specified</SelectItem>
                      {severityLevels.map(level => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="reversibility">Reversibility</Label>
                  <Select
                    value={formData.reversibility}
                    onValueChange={(value) => setFormData({ ...formData, reversibility: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select reversibility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Not specified</SelectItem>
                      {reversibilityOptions.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* High Risk Groups */}
              <div>
                <Label>High Risk Groups</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={riskGroupInput}
                    onChange={(e) => setRiskGroupInput(e.target.value)}
                    placeholder="Add risk group..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('high_risk_groups', riskGroupInput, setRiskGroupInput))}
                  />
                  <Button type="button" variant="outline" onClick={() => addArrayItem('high_risk_groups', riskGroupInput, setRiskGroupInput)}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.high_risk_groups.map((g) => (
                    <Badge key={g} variant="outline" className="cursor-pointer" onClick={() => removeArrayItem('high_risk_groups', g)}>
                      {g} <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Notes & Biomarkers */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="prevalence_notes">Prevalence Notes</Label>
                  <Textarea
                    id="prevalence_notes"
                    value={formData.prevalence_notes}
                    onChange={(e) => setFormData({ ...formData, prevalence_notes: e.target.value })}
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="key_biomarkers">Key Biomarkers</Label>
                  <Textarea
                    id="key_biomarkers"
                    value={formData.key_biomarkers}
                    onChange={(e) => setFormData({ ...formData, key_biomarkers: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>

              {/* References */}
              <div>
                <Label>Source References</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={referenceInput}
                    onChange={(e) => setReferenceInput(e.target.value)}
                    placeholder="Add reference..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('source_references', referenceInput, setReferenceInput))}
                  />
                  <Button type="button" variant="outline" onClick={() => addArrayItem('source_references', referenceInput, setReferenceInput)}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.source_references.map((r) => (
                    <Badge key={r} variant="outline" className="cursor-pointer" onClick={() => removeArrayItem('source_references', r)}>
                      {r} <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Active Switch */}
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {editingNutrient ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search nutrients or symptoms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(searchQuery || filterCategory !== 'all') && (
              <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setFilterCategory('all'); }}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nutrient</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Symptoms Overview</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Reversibility</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredNutrients.map((nutrient) => (
                <TableRow key={nutrient.id}>
                  <TableCell className="font-medium">{nutrient.nutrient}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{nutrient.category}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[300px]">
                    <div className="space-y-1 text-xs">
                      {nutrient.somatic_symptoms?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Heart className="h-3 w-3 text-red-500" />
                          <span className="truncate">{nutrient.somatic_symptoms.slice(0, 2).join('; ')}...</span>
                        </div>
                      )}
                      {nutrient.consciousness_symptoms?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Brain className="h-3 w-3 text-purple-500" />
                          <span className="truncate">{nutrient.consciousness_symptoms.slice(0, 2).join('; ')}...</span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {nutrient.severity && (
                      <Badge variant={getSeverityColor(nutrient.severity)}>
                        {nutrient.severity}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{nutrient.reversibility || '-'}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={nutrient.is_active ? 'default' : 'secondary'}>
                      {nutrient.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(nutrient)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(nutrient.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNutrientDatabase;
